import type { LeadPosition, OfferDraftRow, OfferingPositionComponent, PositionRowId } from '../../../types';
import { isPositionRowId, positionRowIdKey } from './positionRowId';
import { resolveMergedEventualFlag } from './offerEventualPosition';
import { mergeHourlyRateOverride, parseHourlyRateOverride } from './offerLaborPricing';

/** Eine sichtbare Tabellenzeile (Editor + PDF) — gleiche Merge-Logik wie im Angebots-Editor. */
export type MergedOfferTableRow = {
  id: string;
  kind: OfferDraftRow['kind'];
  leadPositionId: PositionRowId | null;
  sourceType: LeadPosition['source_type'] | null;
  quantity: number;
  unit: string;
  item_name: string;
  description: string;
  internal_note: string;
  unit_price_vk: number;
  unit_cost_ek: number;
  surcharge_percent: number;
  labor_minutes: number;
  hourly_rate: number | null;
  image: OfferDraftRow['image'] | null;
  positionSurcharge?: OfferDraftRow['positionSurcharge'];
  isApi: boolean;
  article_number: string | null;
  isEventual: boolean;
  costKind?: OfferDraftRow['costKind'];
  components?: OfferingPositionComponent[];
  metal_kind: LeadPosition['metal_kind'];
  metal_surcharge: number | null;
  metal_priced_ek: number | null;
  titleLevel?: number | null;
};

/**
 * Baut die Positionsliste wie im Angebots-Editor (`combinedRows`):
 * – Reihenfolge folgt `draftRows`, wenn jede Backend-Position (auch Titel/Text/Bild) einen Draft-Rang hat; sonst `sort_order`
 * – Pro Backend-Position höchstens eine Zeile (Draft merged oder reine API-Zeile)
 * – Draft-Overlay gilt auch für `kind: 'apiPosition'` mit `leadPositionId` (z. B. Ledger-Snapshot / Storno-Preise)
 * – Block-Zeilen (`title` / `text` / `image`) mit `leadPositionId` werden auf die verknüpfte API-Position gemerged
 * – Nur „unverlinkte“ Drafts (`leadPositionId` fehlt oder Position existiert nicht) werden angehängt
 *
 * Das PDF nutzte früher `[…uncovered API, …alle Drafts]` — dadurch konnten mehrfach verknüpfte
 * oder verwaiste Draft-Einträge zusätzliche Zeilen erzeugen, die im Editor nicht vorkamen.
 */
export type BuildOfferMergedTableRowsOptions = {
  /**
   * Gutschrift/Storno: entfernte Overlay-Zeilen nicht durch die Katalog-Position ersetzen.
   * Die Tabelle folgt den Draft-Zeilen; ungemergte API-Positionen bleiben unsichtbar.
   */
  omitUncoveredApiPositions?: boolean;
};

/** Infer editor row kind from a backend position when no draft overlay exists. */
export function inferMergedRowKindFromPosition(p: Pick<LeadPosition, 'source_type' | 'unit'>): OfferDraftRow['kind'] {
  const source = String(p.source_type ?? '').trim();
  const unit = String(p.unit ?? '').trim();
  if (source === 'custom_title' || unit === 'Titel') return 'title';
  if (source === 'custom_text') return 'text';
  if (source === 'custom_image') return 'image';
  if (source === 'custom_surcharge') return 'positionSurcharge';
  return 'apiPosition';
}

function unitForInferredKind(kind: OfferDraftRow['kind'], rawUnit: string | null | undefined): string {
  if (kind === 'title') return 'Titel';
  if (kind === 'text' || kind === 'image') return '';
  return (rawUnit ?? 'Stk.').trim() || 'Stk.';
}

function mergedTitleLevel(
  kind: OfferDraftRow['kind'],
  draftLevel: number | null | undefined,
  apiLevel: number | null | undefined,
): number | null {
  if (kind !== 'title') return draftLevel ?? apiLevel ?? null;
  return draftLevel ?? apiLevel ?? 1;
}

export function buildOfferMergedTableRows(
  positions: LeadPosition[],
  draftRows: OfferDraftRow[],
  options?: BuildOfferMergedTableRowsOptions,
): MergedOfferTableRow[] {
  /** Block rows (title/text/image) overlay separately — they are skipped in price/position draft maps. */
  const blockDraftByLeadPos = new Map<string, OfferDraftRow>();
  /** Inkl. apiPosition: Snapshot/Storno speichert oft api-Zeilen — Overlay-Preis muss Vorrang vor Live-DB haben. */
  const draftByLeadPos = new Map<string, OfferDraftRow>();
  for (const row of draftRows) {
    if (!isPositionRowId(row.leadPositionId)) continue;
    const key = positionRowIdKey(row.leadPositionId);
    if (row.kind === 'title' || row.kind === 'text' || row.kind === 'image') {
      blockDraftByLeadPos.set(key, row);
    } else {
      draftByLeadPos.set(key, row);
    }
  }

  const draftPositionRank = new Map<string, number>();
  {
    let rank = 0;
    const positionIds = new Set(positions.map((p) => positionRowIdKey(p.id)));
    for (const row of draftRows) {
      if (!isPositionRowId(row.leadPositionId)) continue;
      const key = positionRowIdKey(row.leadPositionId);
      if (!positionIds.has(key) || draftPositionRank.has(key)) continue;
      draftPositionRank.set(key, rank);
      rank += 1;
    }
  }
  // Draft-Reihenfolge gilt nur, wenn jede Backend-Position einen Draft-Rang hat — inklusive
  // Titel, Text und Bild. Ein teilweiser Entwurf (z. B. eine neue freie Position neben
  // API-Zeilen) darf die sort_order nicht überschreiben.
  const useDraftRankOrder = positions.length > 0 && draftPositionRank.size === positions.length;
  const positionSorted = [...positions].sort((a, b) => {
    if (useDraftRankOrder) {
      const ra = draftPositionRank.get(positionRowIdKey(a.id));
      const rb = draftPositionRank.get(positionRowIdKey(b.id));
      const ia = ra === undefined ? Number.MAX_SAFE_INTEGER : ra;
      const ib = rb === undefined ? Number.MAX_SAFE_INTEGER : rb;
      if (ia !== ib) return ia - ib;
    }
    return (a.sort_order ?? 0) - (b.sort_order ?? 0);
  });

  const mergedFromPositions: MergedOfferTableRow[] = [];
  for (const p of positionSorted) {
    const posKey = positionRowIdKey(p.id);
    const blockDraft = blockDraftByLeadPos.get(posKey);
    if (blockDraft) {
      mergedFromPositions.push({
        id: blockDraft.id,
        kind: blockDraft.kind,
        leadPositionId: blockDraft.leadPositionId ?? p.id,
        sourceType: blockDraft.sourceType ?? p.source_type ?? null,
        quantity: blockDraft.quantity,
        unit: blockDraft.unit,
        item_name: blockDraft.item_name,
        description: blockDraft.description,
        internal_note: blockDraft.internal_note ?? '',
        unit_price_vk: blockDraft.unit_price_vk,
        unit_cost_ek: blockDraft.unit_cost_ek ?? 0,
        surcharge_percent: blockDraft.surcharge_percent ?? 0,
        labor_minutes: blockDraft.labor_minutes ?? p.labor_minutes ?? 0,
        hourly_rate: mergeHourlyRateOverride(blockDraft.hourly_rate, p.hourly_rate),
        image: blockDraft.image ?? null,
        positionSurcharge: blockDraft.positionSurcharge ?? null,
        isApi: false,
        article_number: p.article_number ?? null,
        isEventual: resolveMergedEventualFlag(blockDraft.isEventual, p.is_eventual),
        costKind: blockDraft.costKind,
        components: p.components ?? [],
        metal_kind: p.metal_kind ?? null,
        metal_surcharge: p.metal_surcharge ?? null,
        metal_priced_ek: p.metal_priced_ek ?? null,
        titleLevel: mergedTitleLevel(blockDraft.kind, blockDraft.titleLevel, p.title_level),
      });
      continue;
    }
    const draft = draftByLeadPos.get(posKey);
    if (draft) {
      const kindForTable = draft.kind === 'apiPosition' ? ('freePosition' as const) : draft.kind;
      mergedFromPositions.push({
        id: draft.id,
        kind: kindForTable,
        leadPositionId: draft.leadPositionId ?? p.id,
        sourceType: draft.sourceType ?? p.source_type ?? null,
        quantity: draft.quantity,
        unit: draft.unit,
        item_name: draft.item_name,
        description: draft.description,
        internal_note: draft.internal_note ?? '',
        unit_price_vk: draft.unit_price_vk,
        unit_cost_ek: draft.unit_cost_ek ?? 0,
        surcharge_percent: draft.surcharge_percent ?? 0,
        labor_minutes: draft.labor_minutes ?? p.labor_minutes ?? 0,
        hourly_rate: mergeHourlyRateOverride(draft.hourly_rate, p.hourly_rate),
        image: draft.image ?? null,
        positionSurcharge: draft.positionSurcharge ?? null,
        isApi: false,
        article_number: p.article_number ?? null,
        isEventual: resolveMergedEventualFlag(draft.isEventual, p.is_eventual),
        costKind: draft.costKind,
        components: p.components ?? [],
        metal_kind: p.metal_kind ?? null,
        metal_surcharge: p.metal_surcharge ?? null,
        metal_priced_ek: p.metal_priced_ek ?? null,
        titleLevel: mergedTitleLevel(kindForTable, draft.titleLevel, p.title_level),
      });
      continue;
    }
    if (options?.omitUncoveredApiPositions) continue;
    const kind = inferMergedRowKindFromPosition(p);
    mergedFromPositions.push({
      id: `api-${p.id}`,
      kind,
      leadPositionId: p.id,
      sourceType: p.source_type,
      quantity: p.quantity ?? 0,
      unit: unitForInferredKind(kind, p.unit),
      item_name: p.item_name ?? '',
      description: p.description ?? '',
      internal_note: p.internal_note ?? '',
      unit_price_vk: p.unit_price_vk ?? 0,
      unit_cost_ek: p.unit_cost_ek ?? 0,
      surcharge_percent: p.surcharge_percent ?? 0,
      labor_minutes: p.labor_minutes ?? 0,
      hourly_rate: parseHourlyRateOverride(p.hourly_rate),
      image: null,
      positionSurcharge: null,
      isApi: true,
      article_number: p.article_number ?? null,
      isEventual: p.is_eventual === true,
      components: p.components ?? [],
      metal_kind: p.metal_kind ?? null,
      metal_surcharge: p.metal_surcharge ?? null,
      metal_priced_ek: p.metal_priced_ek ?? null,
      titleLevel: mergedTitleLevel(kind, undefined, p.title_level),
    });
  }

  const unlinkedDraftRows: MergedOfferTableRow[] = draftRows
    .filter((r) => r.kind !== 'apiPosition')
    .filter(
      (r) =>
        !isPositionRowId(r.leadPositionId) ||
        !positions.some((p) => positionRowIdKey(p.id) === positionRowIdKey(r.leadPositionId)),
    )
    .map((r) => ({
      id: r.id,
      kind: r.kind,
      leadPositionId: r.leadPositionId ?? null,
      sourceType: r.sourceType ?? null,
      quantity: r.quantity,
      unit: r.unit,
      item_name: r.item_name,
      description: r.description,
      internal_note: r.internal_note ?? '',
      unit_price_vk: r.unit_price_vk,
      unit_cost_ek: r.unit_cost_ek ?? 0,
      surcharge_percent: r.surcharge_percent ?? 0,
      labor_minutes: r.labor_minutes ?? 0,
      hourly_rate: parseHourlyRateOverride(r.hourly_rate),
      image: r.image ?? null,
      positionSurcharge: r.positionSurcharge ?? null,
      isApi: false,
      article_number: null,
      isEventual: r.isEventual === true,
      costKind: r.costKind,
      components: [],
      metal_kind: null,
      metal_surcharge: null,
      metal_priced_ek: null,
      titleLevel: mergedTitleLevel(r.kind, r.titleLevel, null),
    }));

  return [...mergedFromPositions, ...unlinkedDraftRows];
}

/** Persistierbare Entwurfszeile für eine sichtbare Tabellenzeile (API-Zeilen werden freie Positionen bzw. Blöcke). */
export function draftRowFromMergedTableRow(
  row: Pick<
    MergedOfferTableRow,
    | 'id'
    | 'kind'
    | 'leadPositionId'
    | 'sourceType'
    | 'quantity'
    | 'unit'
    | 'item_name'
    | 'description'
    | 'internal_note'
    | 'unit_price_vk'
    | 'unit_cost_ek'
    | 'surcharge_percent'
    | 'labor_minutes'
    | 'hourly_rate'
    | 'image'
    | 'positionSurcharge'
    | 'isApi'
    | 'isEventual'
    | 'costKind'
    | 'titleLevel'
  >,
): OfferDraftRow {
  const kind = row.kind === 'apiPosition' ? 'freePosition' : row.kind;
  const leadPositionId = row.leadPositionId;
  const id =
    row.isApi && isPositionRowId(leadPositionId) ? positionRowIdKey(leadPositionId) : row.id;
  return {
    id,
    kind,
    leadPositionId,
    sourceType: row.sourceType,
    quantity: row.quantity,
    unit: row.unit,
    item_name: row.item_name,
    description: row.description,
    internal_note: row.internal_note,
    unit_price_vk: row.unit_price_vk,
    unit_cost_ek: row.unit_cost_ek,
    surcharge_percent: row.surcharge_percent,
    labor_minutes: row.labor_minutes,
    hourly_rate: row.hourly_rate,
    image: row.image,
    positionSurcharge: row.positionSurcharge ?? null,
    isEventual: row.isEventual,
    ...(row.costKind ? { costKind: row.costKind } : {}),
    ...(row.kind === 'title' ? { titleLevel: row.titleLevel ?? 1 } : {}),
  };
}

/**
 * Ergänzt Entwurfszeilen für Positionen, die noch keinen Entwurf haben.
 * Die Reihenfolge folgt der aktuell sichtbaren Tabelle (`sort_order`, solange die Abdeckung unvollständig ist).
 * Vorhandene Entwürfe bleiben erhalten. Bei voller Abdeckung wird nichts umsortiert.
 */
export function ensureDraftRowsCoverPositions(
  positions: LeadPosition[],
  draftRows: OfferDraftRow[],
  options?: BuildOfferMergedTableRowsOptions,
): { rows: OfferDraftRow[]; changed: boolean } {
  if (positions.length === 0) return { rows: draftRows, changed: false };

  const positionIds = new Set(positions.map((p) => positionRowIdKey(p.id)));
  const draftByLead = new Map<string, OfferDraftRow>();
  for (const row of draftRows) {
    if (!isPositionRowId(row.leadPositionId)) continue;
    const key = positionRowIdKey(row.leadPositionId);
    if (!positionIds.has(key) || draftByLead.has(key)) continue;
    draftByLead.set(key, row);
  }

  const merged = buildOfferMergedTableRows(positions, draftRows, options);
  const missing = merged.some(
    (row) =>
      isPositionRowId(row.leadPositionId) &&
      positionIds.has(positionRowIdKey(row.leadPositionId)) &&
      !draftByLead.has(positionRowIdKey(row.leadPositionId)),
  );
  if (!missing) return { rows: draftRows, changed: false };

  const draftById = new Map(draftRows.map((row) => [row.id, row]));
  const used = new Set<string>();
  const next: OfferDraftRow[] = [];
  for (const row of merged) {
    const leadKey = isPositionRowId(row.leadPositionId) ? positionRowIdKey(row.leadPositionId) : null;
    const existing =
      (leadKey ? draftByLead.get(leadKey) : undefined) ?? draftById.get(row.id);
    if (existing && !used.has(existing.id)) {
      next.push(existing);
      used.add(existing.id);
      continue;
    }
    if (!leadKey || !positionIds.has(leadKey)) continue;
    const created = draftRowFromMergedTableRow(row);
    if (used.has(created.id)) continue;
    next.push(created);
    used.add(created.id);
  }
  return { rows: next, changed: true };
}
